'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import { useDensity } from '@liftango/ui-tokens';

export type PillButtonVariant = 'default' | 'primary' | 'active';

export interface PillButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  variant?: PillButtonVariant;
  active?: boolean;
}

const variantStyles = (variant: PillButtonVariant, active: boolean, theme: any) => {
  if (variant === 'primary')
    return css`
      background: ${theme.colors.brand.primary};
      color: #fff;
      border: none;
      &:hover:not(:disabled) {
        background: ${theme.colors.brand.primaryHover};
      }
    `;
  if (active || variant === 'active')
    return css`
      background: ${theme.colors.brand.primarySoft};
      color: ${theme.colors.brand.primary};
      border: 1px solid ${theme.colors.brand.primary};
      &:hover:not(:disabled) {
        background: ${theme.colors.brand.primarySoft};
      }
    `;
  return css`
    background: ${theme.colors.neutral.panel};
    color: ${theme.colors.neutral.ink};
    border: 1px solid ${theme.colors.neutral.line};
    &:hover:not(:disabled) {
      background: ${theme.colors.neutral.panelAlt};
    }
  `;
};

const Button = styled.button<{ $variant: PillButtonVariant; $active: boolean; $height: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: ${(p) => p.$height}px;
  padding: 0 14px;
  border-radius: ${(p) => p.theme.radius.md};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  font-family: ${(p) => p.theme.fontFamily.ui};
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
  transition:
    background ${(p) => p.theme.transition.color},
    color ${(p) => p.theme.transition.color};
  ${(p) => variantStyles(p.$variant, p.$active, p.theme)}
  ${focusRing}
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(function PillButton(
  { icon, variant = 'default', active = false, children, ...rest },
  ref,
) {
  const density = useDensity();
  return (
    <Button
      ref={ref}
      $variant={variant}
      $active={active}
      $height={density.controlHeight}
      aria-pressed={active || undefined}
      {...rest}
    >
      {icon}
      {children}
    </Button>
  );
});
